// src/pages/admin/TestimonialApprovalDashboard.tsx
import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, 
  faTimesCircle, 
  faClock, 
  faQuoteLeft, 
  faTrashAlt,
  faSpinner,
  faEye,
  faUndoAlt
} from '@fortawesome/free-solid-svg-icons';
import { 
  db 
} from '../../firebase'; 
import { 
  collection, 
  query, 
  getDocs, 
  updateDoc, 
  doc, 
  deleteDoc, 
  orderBy,
  Timestamp,
  DocumentData
} from 'firebase/firestore';

// --- Interfaces (Re-using/Extending from FeedBackPage.tsx) ---
interface Testimony extends DocumentData {
  id: string;
  name: string;
  testimony: string;
  createdAt: Timestamp;
  approved: boolean; // Must be saved on document submission
}

// --- Styled Components (Reusing existing concepts and colors) ---

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const DashboardContainer = styled.div`
  padding: 40px 20px;
  max-width: 1400px;
  margin: 0 auto;
  animation: ${fadeIn} 0.5s ease-out;
`;

const Title = styled.h2`
  font-size: 2.2rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 30px;
  border-bottom: 3px solid #6c63ff;
  padding-bottom: 10px;
`;

const SectionTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #4a5568;
  margin: 30px 0 15px;
  display: flex;
  align-items: center;
  gap: 10px;

  ${({ color }) => color === 'green' && css`color: #38a169;`}
  ${({ color }) => color === 'red' && css`color: #e53e3e;`}
  ${({ color }) => color === 'blue' && css`color: #6c63ff;`}
`;

const TableWrapper = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  overflow-x: auto;
  margin-bottom: 40px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;

  th, td {
    padding: 15px;
    text-align: left;
    border-bottom: 1px solid #f0f0f0;
  }

  th {
    background-color: #f8f9fa;
    color: #555;
    font-weight: 600;
    text-transform: uppercase;
  }

  tr:hover {
    background-color: #f5f5f5;
  }
`;

const StatusBadge = styled.span<{ $approved: boolean }>`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.8rem;
  
  ${({ $approved }) => $approved 
    ? css`background-color: #c6f6d5; color: #276749;` // Approved (Green)
    : css`background-color: #fff3cd; color: #975a16;` // Pending (Yellow/Amber)
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

interface ActionButtonProps {
    $type: 'approve' | 'reject' | 'view' | 'revert';
    disabled: boolean;
}

const ActionButton = styled.button<ActionButtonProps>`
  background: none;
  border: 1px solid;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 5px;

  ${({ $type }) => {
    switch ($type) {
      case 'approve':
        return css`
          color: #38a169;
          border-color: #38a169;
          &:hover { background-color: #38a169; color: white; }
        `;
      case 'reject':
        return css`
          color: #e53e3e;
          border-color: #e53e3e;
          &:hover { background-color: #e53e3e; color: white; }
        `;
      case 'revert':
        return css`
          color: #6c63ff;
          border-color: #6c63ff;
          &:hover { background-color: #6c63ff; color: white; }
        `;
      default: // view
        return css`
          color: #4a5568;
          border-color: #e2e8f0;
          &:hover { background-color: #e2e8f0; }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Modal Styles (simplified, assumed from previous context)
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #fff;
  padding: 30px;
  border-radius: 10px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  position: relative;
  max-height: 80vh;
  overflow-y: auto;
`;

const TestimonyQuote = styled.p`
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border-left: 4px solid #6c63ff;
  font-style: italic;
  line-height: 1.6;
  margin-top: 20px;
  position: relative;
  font-size: 1.05rem;

  &::before {
    content: '';
    position: absolute;
    top: -15px;
    left: 20px;
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-bottom: 15px solid #6c63ff;
  }
`;

// --- Component ---

export const TestimonialApprovalDashboard: React.FC = () => {
  const [testimonials, setTestimonials] = useState<Testimony[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTestimony, setSelectedTestimony] = useState<Testimony | null>(null);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      setError(null);
      const q = query(collection(db, 'testimonies'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const fetchedTestimonials: Testimony[] = querySnapshot.docs.map(doc => ({
        id: doc.id,
        // Ensure default 'approved' status if missing
        approved: doc.data().approved || false, 
        name: doc.data().name || 'Anonymous',
        testimony: doc.data().testimony || '[Testimony missing]',
        createdAt: doc.data().createdAt as Timestamp,
      } as Testimony));
      
      setTestimonials(fetchedTestimonials);
    } catch (err: any) {
      console.error("Error fetching testimonials:", err);
      setError("Failed to load testimonials: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const handleUpdateStatus = async (testimonyId: string, newStatus: boolean) => {
    try {
      setLoading(true);
      const docRef = doc(db, 'testimonies', testimonyId);
      await updateDoc(docRef, { approved: newStatus });

      // Update local state without full re-fetch for better UX
      setTestimonials(prev => prev.map(t => 
        t.id === testimonyId ? { ...t, approved: newStatus } : t
      ));
      setSelectedTestimony(null); // Close modal if open

    } catch (e) {
      console.error("Error updating status:", e);
      alert(`Failed to ${newStatus ? 'approve' : 'revert'} testimonial.`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (testimonyId: string) => {
    if (!window.confirm('Are you sure you want to permanently delete this testimonial?')) {
      return;
    }

    try {
      setLoading(true);
      const docRef = doc(db, 'testimonies', testimonyId);
      await deleteDoc(docRef);

      // Remove from local state
      setTestimonials(prev => prev.filter(t => t.id !== testimonyId));
      setSelectedTestimony(null);
      
    } catch (e) {
      console.error("Error deleting document:", e);
      alert('Failed to delete testimonial.');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp: Timestamp) => {
    if (!timestamp) return 'Date unknown';
    return timestamp.toDate().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const pendingTestimonials = testimonials.filter(t => !t.approved);
  const approvedTestimonials = testimonials.filter(t => t.approved);

  // --- Render ---
  if (loading) return (
    <DashboardContainer style={{ textAlign: 'center', paddingTop: '100px' }}>
      <FontAwesomeIcon icon={faSpinner} spin size="2x" color="#6c63ff" />
      <p style={{ marginTop: '20px', color: '#4a5568' }}>Loading testimonials...</p>
    </DashboardContainer>
  );

  if (error) return <DashboardContainer style={{ color: '#e53e3e' }}>Error: {error}</DashboardContainer>;

  return (
    <DashboardContainer>
      <Title>🗣️ Testimonial Approval Dashboard</Title>

      {/* --- Pending Section --- */}
      <SectionTitle color="blue">
        <FontAwesomeIcon icon={faClock} /> Pending Approval ({pendingTestimonials.length})
      </SectionTitle>
      <TableWrapper>
        <Table>
          <thead>
            <tr>
              <th>Submitted By</th>
              <th>Date</th>
              <th>Excerpt</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pendingTestimonials.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: '20px' }}>🎉 No new testimonials pending approval!</td></tr>
            ) : (
              pendingTestimonials.map(t => (
                <tr key={t.id}>
                  <td>{t.name}</td>
                  <td>{formatTimestamp(t.createdAt)}</td>
                  <td>{t.testimony.substring(0, 80)}{t.testimony.length > 80 ? '...' : ''}</td>
                  <td><StatusBadge $approved={t.approved}>Pending</StatusBadge></td>
                  <td>
                    <ButtonGroup>
                      <ActionButton $type="approve" onClick={() => handleUpdateStatus(t.id, true)} disabled={loading}>
                        <FontAwesomeIcon icon={faCheckCircle} /> Approve
                      </ActionButton>
                      <ActionButton $type="reject" onClick={() => handleDelete(t.id)} disabled={loading}>
                        <FontAwesomeIcon icon={faTrashAlt} /> Delete
                      </ActionButton>
                      <ActionButton $type="view" onClick={() => setSelectedTestimony(t)} disabled={loading}>
                        <FontAwesomeIcon icon={faEye} /> View
                      </ActionButton>
                    </ButtonGroup>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </TableWrapper>

      {/* --- Approved Section --- */}
      <SectionTitle color="green">
        <FontAwesomeIcon icon={faCheckCircle} /> Approved Testimonials ({approvedTestimonials.length})
      </SectionTitle>
      <TableWrapper>
        <Table>
          <thead>
            <tr>
              <th>Submitted By</th>
              <th>Date</th>
              <th>Excerpt</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {approvedTestimonials.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: '20px' }}>No approved testimonials found.</td></tr>
            ) : (
              approvedTestimonials.map(t => (
                <tr key={t.id}>
                  <td>{t.name}</td>
                  <td>{formatTimestamp(t.createdAt)}</td>
                  <td>{t.testimony.substring(0, 80)}{t.testimony.length > 80 ? '...' : ''}</td>
                  <td><StatusBadge $approved={t.approved}>Approved</StatusBadge></td>
                  <td>
                    <ButtonGroup>
                      <ActionButton $type="revert" onClick={() => handleUpdateStatus(t.id, false)} disabled={loading}>
                        <FontAwesomeIcon icon={faUndoAlt} /> Revert
                      </ActionButton>
                      <ActionButton $type="reject" onClick={() => handleDelete(t.id)} disabled={loading}>
                        <FontAwesomeIcon icon={faTrashAlt} /> Delete
                      </ActionButton>
                      <ActionButton $type="view" onClick={() => setSelectedTestimony(t)} disabled={loading}>
                        <FontAwesomeIcon icon={faEye} /> View
                      </ActionButton>
                    </ButtonGroup>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </TableWrapper>
      
      {/* --- Full View Modal --- */}
      {selectedTestimony && (
        <ModalOverlay onClick={() => setSelectedTestimony(null)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <h3 style={{ color: '#6c63ff' }}>Full Testimony View</h3>
            <hr style={{ margin: '10px 0 20px' }} />
            <p><strong>Name:</strong> {selectedTestimony.name}</p>
            <p><strong>Submitted:</strong> {formatTimestamp(selectedTestimony.createdAt)}</p>
            <p><strong>Status:</strong> <StatusBadge $approved={selectedTestimony.approved}>{selectedTestimony.approved ? 'Approved' : 'Pending'}</StatusBadge></p>
            
            <TestimonyQuote>
              <FontAwesomeIcon icon={faQuoteLeft} style={{ marginRight: '8px', color: '#48c6ef' }} />
              {selectedTestimony.testimony}
            </TestimonyQuote>
            
            <ButtonGroup style={{ justifyContent: 'flex-end', marginTop: '20px' }}>
              <ActionButton $type="reject" onClick={() => handleDelete(selectedTestimony.id)} disabled={loading}>
                <FontAwesomeIcon icon={faTrashAlt} /> Delete
              </ActionButton>
              {!selectedTestimony.approved ? (
                <ActionButton $type="approve" onClick={() => handleUpdateStatus(selectedTestimony.id, true)} disabled={loading}>
                  <FontAwesomeIcon icon={faCheckCircle} /> Approve
                </ActionButton>
              ) : (
                <ActionButton $type="revert" onClick={() => handleUpdateStatus(selectedTestimony.id, false)} disabled={loading}>
                  <FontAwesomeIcon icon={faUndoAlt} /> Revert to Pending
                </ActionButton>
              )}
            </ButtonGroup>
          </ModalContent>
        </ModalOverlay>
      )}
    </DashboardContainer>
  );
};